import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, Save, MessageSquareText } from "lucide-react";
import { toast } from "sonner";

const VARS_HELP = ["customer_name", "invoice_number", "amount", "due_date", "item_names", "paid_amount", "outstanding_amount", "cutoff_date", "today_date"];

export default function Templates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ name: "", body: "", pancake_flow_id: "" });
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data } = await api.get("/templates");
      if (alive) {
        setItems(data);
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [reloadKey]);

  const startEdit = (tpl) => {
    setEditing(tpl.key);
    setDraft({ name: tpl.name, body: tpl.body, pancake_flow_id: tpl.pancake_flow_id || "" });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/templates/${editing}`, draft);
      toast.success("Template tersimpan");
      setEditing(null);
      setReloadKey((k) => k + 1);
    } catch (e) {
      toast.error("Gagal menyimpan: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center gap-2 text-sm text-[#64748B]">
        <Loader2 className="w-4 h-4 animate-spin" /> Memuat...
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="templates-page">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-[#0F172A]">Templates</h1>
        <p className="text-sm text-[#64748B] mt-1">
          Atur isi pesan WhatsApp untuk invoice baru & berbagai tahap reminder pembayaran.
        </p>
      </div>

      <div className="mb-4 p-3 bg-[#F1F5F9] rounded-md text-xs text-[#475569]">
        Variabel yang tersedia:{" "}
        {VARS_HELP.map((v) => (
          <code key={v} className="font-mono bg-white border border-[#E2E8F0] rounded px-1.5 py-0.5 mx-1">
            {"{{" + v + "}}"}
          </code>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((tpl) => (
          <div
            key={tpl.key}
            className="border border-[#E2E8F0] rounded-lg bg-white p-5"
            data-testid={`template-card-${tpl.key}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-[#EFF6FF] flex items-center justify-center">
                  <MessageSquareText className="w-4 h-4 text-[#0066FF]" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="text-sm font-medium text-[#0F172A]">{tpl.name}</div>
                  <div className="text-xs text-[#64748B] font-mono">{tpl.key}</div>
                </div>
              </div>
              {editing !== tpl.key && (
                <button
                  onClick={() => startEdit(tpl)}
                  data-testid={`edit-template-${tpl.key}`}
                  className="text-xs text-[#0066FF] hover:underline"
                >
                  Edit
                </button>
              )}
            </div>

            {editing === tpl.key ? (
              <div className="space-y-3">
                <input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Nama Template"
                  className="w-full bg-white border border-[#E2E8F0] rounded-md px-3 py-2 text-sm focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none"
                />
                <input
                  value={draft.pancake_flow_id}
                  onChange={(e) => setDraft({ ...draft, pancake_flow_id: e.target.value })}
                  placeholder="Botcake Flow ID (opsional jika teks)"
                  className="w-full bg-white border border-[#E2E8F0] rounded-md px-3 py-2 text-sm focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none"
                />
                <textarea
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  rows={8}
                  data-testid={`template-body-${tpl.key}`}
                  className="w-full bg-white border border-[#E2E8F0] rounded-md px-3 py-2 text-sm font-mono focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] outline-none"
                />
                <div className="flex gap-2">
                  <button
                    onClick={save}
                    disabled={saving}
                    data-testid={`save-template-${tpl.key}`}
                    className="bg-[#0A2540] text-white hover:bg-[#1E3A8A] rounded-md px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Simpan
                  </button>
                  <button
                    onClick={() => setEditing(null)}
                    className="bg-white border border-[#E2E8F0] text-[#0F172A] hover:bg-[#F8F9FA] rounded-md px-4 py-2 text-sm font-medium transition-colors"
                  >
                    Batal
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {tpl.pancake_flow_id && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-semibold text-[#64748B]">Botcake Flow ID:</span>
                    <span className="bg-[#E2E8F0] text-[#0F172A] px-2 py-0.5 rounded font-mono">{tpl.pancake_flow_id}</span>
                  </div>
                )}
                <pre className="text-xs text-[#475569] font-mono whitespace-pre-wrap bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-3 leading-relaxed">
                  {tpl.body}
                </pre>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
